import { FC, useCallback, useMemo } from "react";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Skeleton,
  Stack,
  SxProps,
  Typography,
} from "@mui/material";
import { cacheService, mediaService, thumbnailService } from "../../../Core/Api/services";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { FolderOutlined } from "@mui/icons-material";
import AssetTypeChip from "../../Misc/Chips/AssetChip/asset-type-chip";
import AssetThumbnail from "../../Misc/AssetThumbnail";
import { useNavigate } from "react-router";
import { routePaths } from "../../../Router/router";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import moment from "moment";
import { AssetTypeAlphaColor } from "../../../Utils/helpers";

interface ISearchCardProps {
  asset: DbAssetObjectInfos;
  onClick?: () => void;
  endAdornment?: React.ReactNode;
  sx?: SxProps;
}

export const SearchCard: FC<ISearchCardProps> = (props) => {
  const { asset, endAdornment, sx, onClick } = props;
  const navigate = useNavigate();

  const metadata: DbMetadataFieldInfos[] = cacheService.GetMetadata();

  const handleCardClick = useCallback(() => {
    // If there are card actions, they override the default click action
    if (onClick) onClick();
    // Default: navigate to asset
    else if (asset.layer === ObjectLayer.Asset) {
      if (asset.classification === ObjectClass.Drive) navigate(`${routePaths.drive.path}/${asset.id}`);
      else navigate(`${routePaths.asset.path}/${asset.id}`);
    }
  }, [asset, onClick]);

  const _thumbnail = useMemo(() => {
    let url = "";

    if (asset.classification === ObjectClass.Drive)
      url = thumbnailService.GetObjectPosterUrl(asset.id ?? -1, ThumbnailSize.medium);
    else if (asset.layer === ObjectLayer.Content || asset.layer === ObjectLayer.Title)
      url = thumbnailService.GetObjectPosterUrl(asset.id ?? -1, ThumbnailSize.medium);
    else if (asset.layer === ObjectLayer.Asset)
      url = thumbnailService.GetObjectPosterUrl(asset.parentTitleId ?? asset.id, ThumbnailSize.medium);

    return (
      <Box
        sx={{
          width: 54,
          height: "100%",
          overflow: "hidden",
          position: "relative",

          ...((asset.layer === ObjectLayer.Asset || asset.layer === ObjectLayer.Clip) && {
            backgroundColor: AssetTypeAlphaColor(asset.type, 0.5),
          }),

          "&:before": {
            content: "''",
            position: "absolute",
            zIndex: 0,
            display: "block",
            width: "100%",
            height: "100%",
            opacity: asset.layer === ObjectLayer.Asset || asset.layer === ObjectLayer.Clip ? 0.5 : 1,
            backgroundImage: url ? `url(${url})` : "none",
            backgroundSize: "cover",
            backgroundPosition: "center center",
          },
        }}
      >
        {(asset.layer === ObjectLayer.Asset || asset.layer === ObjectLayer.Clip) && (
          <AssetThumbnail
            type={asset.type}
            size="micro"
            sx={{ position: "absolute", bottom: 4, left: 4 }}
            // sx={{ width: "100%", height: "100%", borderRadius: 0 }}
          />
        )}
      </Box>
    );
  }, [asset]);

  const _chips = useMemo(() => {
    if (asset.layer === ObjectLayer.Title) {
      let productionDate: string | null = GetMetadataByTagName(asset, "productionDate", metadata)?.value ?? null;
      let duration: string | null = GetMetadataByTagName(asset, "duration", metadata)?.value ?? null;
      let titleType: string | null = GetMetadataByTagName(asset, "titleType", metadata)?.value ?? null;

      if (duration) {
        if (duration.substring(0, 2) === "00") duration = `${duration.substring(3, 5)}:${duration.substring(6, 8)}`;
        else duration = `${parseInt(duration.substring(0, 2))}h${duration.substring(3, 5)}`;
      }

      return (
        <Stack direction="row" spacing={0.5}>
          {titleType ? <Chip size="small" label={titleType} /> : <></>}
          {duration ? <Chip size="small" label={duration} /> : <></>}
          {productionDate ? <Chip size="small" label={moment(productionDate).format("YYYY")} /> : <></>}
        </Stack>
      );
    } else {
      let duration: string | null = GetMetadataByTagName(asset, "durationSMPTE")?.value ?? null;
      let wrapper: string | null = GetMetadataByTagName(asset, "techWrapper")?.value ?? null;

      if (asset.type === ObjectType.Unknown) return <></>;

      if (duration) {
        if (duration.substring(0, 2) === "00") duration = `${duration.substring(3, 5)}:${duration.substring(6, 8)}`;
        else duration = `${parseInt(duration.substring(0, 2))}h${duration.substring(3, 5)}`;
      }

      return (
        <Stack direction="row" spacing={0.5}>
          {asset.type === ObjectType.Folder || asset.type === ObjectType.Group ? (
            <Chip size="small" color="accent" variant="outlined" label={asset.childCount} icon={<FolderOutlined />} />
          ) : (
            <AssetTypeChip size="small" variant="outlined" label={wrapper ?? undefined} type={asset.type} />
          )}
          {duration ? <Chip size="small" label={duration} /> : <></>}
        </Stack>
      );
    }
  }, [asset]);

  const _content = useMemo(() => {
    if (asset.layer === ObjectLayer.Clip) {
      return (
        <>
          <Typography variant="body1" color="text.primary" sx={(t) => ({ ...t.helpers(t).ellipsis })}>
            {asset.name}
          </Typography>
          <Stack direction="row" gap={0.5} alignItems={"center"} pt={0.5}>
            {_chips}
            <Typography variant="body2" color="text.tier" sx={(t) => ({ ...t.helpers(t).ellipsis })}>
              {asset.parentObject?.name}
            </Typography>
          </Stack>
        </>
      );
    } else if (asset.classification === ObjectClass.Drive) {
      return (
        <>
          <Typography variant="body1" color="text.primary" sx={(t) => ({ ...t.helpers(t).ellipsis })}>
            {asset.name}
          </Typography>
          <Stack direction="row" gap={0.5} alignItems={"center"} pt={0.5}>
            {_chips}
            <Typography variant="body2" color="text.tier">
              #{asset.id}
            </Typography>
          </Stack>
        </>
      );
    } else if (asset.layer === ObjectLayer.Title) {
      return (
        <>
          <Typography variant="body1" color="text.primary" sx={(t) => ({ ...t.helpers(t).ellipsis })}>
            {asset.name}
          </Typography>
          <Stack direction="row" gap={0.5} alignItems={"center"} pt={0.5}>
            {_chips}
          </Stack>
        </>
      );
    } else
      return (
        <>
          <Typography variant="body1" color="text.primary" sx={(t) => ({ ...t.helpers(t).ellipsis })}>
            {asset.parentTitleName} / {asset.parentObject?.name}
          </Typography>
          <Typography variant="body2" color="text.tier" sx={(t) => ({ ...t.helpers(t).clampedText })}>
            {asset.name}
          </Typography>
        </>
      );
  }, [asset]);

  return (
    <Card variant="outlined" sx={{ height: 80, borderRadius: 1, ...sx }} onClick={handleCardClick}>
      <CardActionArea sx={(t) => ({ height: "100%" })} disableRipple={onClick === undefined}>
        <Box sx={(t) => ({ display: "flex", flexDirection: "row", alignItems: "stretch", height: "100%" })}>
          <CardMedia sx={{ width: 52 }}>{_thumbnail}</CardMedia>
          <Stack
            sx={(t) => ({
              flex: "1 1 0",
              minWidth: 0,
              py: t.spacing(1.5),
              px: t.spacing(2),
              justifyContent: "center",
              alignItems: "flex-start",
              gap: 0.5,
            })}
          >
            {_content}
          </Stack>
          {endAdornment && <Stack sx={(t) => ({ justifyContent: "center", pr: t.spacing(2) })}>{endAdornment}</Stack>}
        </Box>
      </CardActionArea>
    </Card>
  );
};

export const SearchCardSkeleton = () => {
  return (
    <Card variant="outlined" sx={{ height: 80, borderRadius: 1 }}>
      <Box sx={(t) => ({ display: "flex", flexDirection: "row", alignItems: "stretch", height: "100%" })}>
        <Skeleton variant="rectangular" sx={{ width: 52, height: 80 }} />
        <Stack
          sx={(t) => ({
            flex: "1 1 0",
            minWidth: 0,
            py: t.spacing(1.5),
            px: t.spacing(2),
            justifyContent: "center",
            alignItems: "flex-start",
            gap: 0.5,
          })}
        >
          <Skeleton variant="text" sx={(t) => ({ ...t.typography.body1, width: "66%" })} />
          <Skeleton variant="text" sx={(t) => ({ ...t.typography.body2, width: "33%" })} />
        </Stack>
      </Box>
    </Card>
  );
};
